/**
 * La boussole: le nord ET le cap vers un autre joueur, sur UN SEUL cadran.
 *
 * POURQUOI ELLE EXISTE. La camera pivote librement (deux doigts sur le
 * telephone, glissement sur le ciel a la souris): apres trois rotations, plus
 * personne ne sait de quel cote est le glacier. Un repere permanent coute
 * quelques `<div>` et rend la carte mentale du monde a nouveau utilisable.
 *
 * POURQUOI UN SEUL CADRAN. Il y en a eu deux, empiles: un disque pour le nord,
 * une pastille dessous pour l'autre joueur. Deux boussoles pour une seule
 * question — « de quel cote ? » — et l'oeil devait choisir laquelle lire. Tout
 * tient donc maintenant sur le meme disque, partage par RAYON: le nord vit
 * DEDANS (l'aiguille rouge, puis la lettre juste au-dela de sa pointe), le
 * joueur vise vit DEHORS, une pastille ronde a SA couleur de compte posee sur
 * le lisere. Deux formes et deux rayons: on ne les confond pas, et surtout elles
 * ne se cachent pas l'une l'autre quand le joueur vise est PILE au nord — les
 * poser sur des rayons voisins faisait disparaitre tantot la lettre, tantot la
 * pastille, et c'est exactement le cas qu'un joueur regarde.
 *
 * POURQUOI EN DOM. Une aiguille dessinee dans la scene voudrait dire de la
 * geometrie de plus, donc des appels de dessin sur un budget deja compte (voir
 * CLAUDE.md), et elle prendrait la lumiere de la scene — donc illisible la nuit,
 * pile quand on se perd. Quelques elements positionnes ne coutent rien a la
 * carte graphique et restent lisibles a toute heure.
 *
 * ON LA TOUCHE, ET ELLE FAIT LE TOUR DES CONNECTES. Le tour suit l'ordre des
 * identifiants, jamais la distance: un ordre qui se reordonne pendant qu'on
 * marche ferait sauter la cible d'un clic a l'autre. Le tour compte un cran de
 * plus qu'il n'y a de joueurs — apres le dernier on ne vise plus personne — pour
 * qu'on puisse ranger la marque, et pour qu'a deux joueurs le clic ait quand
 * meme un effet visible.
 *
 * LE PIEGE, ET C'EST LE COEUR DU FICHIER. La vue est orthographique et penchee
 * de 45 degres: l'axe de profondeur du monde est ECRASE a l'ecran d'un facteur
 * sin(45) = 0,71. Faire simplement tourner l'aiguille de l'azimut de la camera
 * se trompe donc jusqu'a une dizaine de degres — assez pour envoyer quelqu'un a
 * cote. `angleEcran` projette la direction du monde sur les VRAIS axes de
 * l'ecran de la camera; c'est verrouille par un test qui la compare a la
 * projection de three.js (tests/unit/boussole.test.ts).
 *
 *   const boussole = new Boussole(couche)
 *   boussole.maj(rig.azimut, joueur.pos, renduJoueurs.presents(), rig.camera)
 */
import * as THREE from 'three'
import { CAM_ELEVATION } from '../../shared/constants.ts'
import { journal } from '../log/journal.ts'
import { clicSonore, el, hex } from './commun.ts'
import '../styles/boussole.css'

/** Direction du nord dans le monde. L'ouest est en -x, le nord en -z. */
export const NORD = Object.freeze({ dx: 0, dz: -1 })

/** Un point du monde, tel que le rendu des autres joueurs en fournit. */
export interface PointMonde {
  readonly x: number
  readonly y: number
  readonly z: number
}

/** Un joueur connecte, donc une cible possible du cadran. */
export interface JoueurVise extends PointMonde {
  /** Identifiant de liaison: c'est lui qui donne l'ordre du tour. */
  readonly id: string
  readonly nom?: string
  /** Couleur du compte (0xRRGGBB), celle de son socle dans le monde. */
  readonly couleur?: number
}

/** Ce qu'il faut savoir pour pointer vers quelqu'un. */
export interface Cap {
  readonly id: string
  /** Ecart en metres sur x, du joueur local vers l'autre. */
  dx: number
  /** Ecart en metres sur z. */
  dz: number
  /** Distance horizontale en metres. */
  distance: number
  /** Nom affiche a cote de la distance, si on l'a. */
  nom?: string
  /** Couleur du compte vise, si on l'a. */
  couleur?: number
}

/**
 * Marge d'ecran, en fraction du demi-frustum.
 *
 * On declare l'autre « a l'ecran » seulement s'il est confortablement dedans:
 * a 0, un joueur colle au bord ferait clignoter l'indication a chaque pas.
 */
const MARGE_ECRAN = 0.1

// ------------------------------------------------------------------- calcul

/**
 * Angle a l'ecran d'une direction du monde, en degres, sens horaire depuis le
 * haut de l'ecran — exactement ce qu'attend `transform: rotate()`.
 *
 * La camera regarde la cible depuis l'azimut donne, penchee de CAM_ELEVATION.
 * Ses axes ecran valent alors, en coordonnees du monde:
 *   droite = ( sin(az), 0, -cos(az) )
 *   haut   = ( -sin(el) cos(az), cos(el), -sin(el) sin(az) )
 * Il ne reste qu'a projeter (dx, 0, dz) sur ces deux axes. Le `sin(el)` du
 * second est l'ecrasement de la profondeur: c'est lui qu'on oublie quand on
 * croit qu'une simple rotation de l'azimut suffit.
 */
export function angleEcran(azimut: number, dx: number, dz: number): number {
  const ca = Math.cos(azimut)
  const sa = Math.sin(azimut)
  const versDroite = dx * sa - dz * ca
  const versHaut = -Math.sin(CAM_ELEVATION) * (dx * ca + dz * sa)
  return (Math.atan2(versDroite, versHaut) * 180) / Math.PI
}

/** Vecteur de travail: la question se pose a chaque image, on n'alloue pas. */
const point = new THREE.Vector3()

/** Vrai quand ce point du monde se voit franchement a l'ecran. */
export function visibleAEcran(p: PointMonde, camera: THREE.Camera, marge = MARGE_ECRAN): boolean {
  const v = point.set(p.x, p.y, p.z).project(camera)
  const bord = 1 - marge
  return v.z <= 1 && Math.abs(v.x) <= bord && Math.abs(v.y) <= bord
}

/**
 * L'ordre du tour: celui des identifiants, et rien d'autre.
 *
 * La tentation serait de trier par distance, « le plus proche d'abord ». Ce
 * serait un ordre qui se remelange a chaque pas: deux clics de suite ne
 * visiteraient pas deux joueurs differents, et personne ne pourrait apprendre
 * le geste. L'identifiant, lui, ne bouge jamais de la session.
 */
export function ordonnerVises(autres: readonly JoueurVise[]): JoueurVise[] {
  return [...autres].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
}

/**
 * La cible d'apres, au clic. `null` est un cran du tour a part entiere: apres
 * le dernier joueur, on ne vise plus personne, puis on repart du premier.
 */
export function cibleSuivante(ids: readonly string[], courant: string | null): string | null {
  if (ids.length === 0) return null
  const i = courant === null ? -1 : ids.indexOf(courant)
  if (i < 0) return ids[0] ?? null
  return i + 1 < ids.length ? (ids[i + 1] ?? null) : null
}

/**
 * La cible quand celle qu'on visait s'est deconnectee.
 *
 * On ne revient pas au premier de la liste: on prend le premier identifiant qui
 * SUIT celui qui est parti, comme si le tour avait continue tout seul. Rester
 * sur un fantome, ou repartir du debut a chaque depart, sont les deux facons de
 * se retrouver bloque.
 */
export function cibleDeRepli(ids: readonly string[], parti: string): string | null {
  for (const id of ids) if (id > parti) return id
  return ids[0] ?? null
}

/** Le cap du joueur local vers cette cible. */
export function capVers(moi: { x: number; z: number }, cible: JoueurVise): Cap {
  const dx = cible.x - moi.x
  const dz = cible.z - moi.z
  const cap: Cap = { id: cible.id, dx, dz, distance: Math.hypot(dx, dz) }
  if (cible.nom !== undefined) cap.nom = cible.nom
  if (cible.couleur !== undefined) cap.couleur = cible.couleur
  return cap
}

/** Distance ecrite pour un humain: des metres, puis des kilometres. */
export function formaterDistance(m: number): string {
  if (!Number.isFinite(m) || m < 0) return '—'
  if (m < 1000) return `${Math.round(m)} m`
  return `${(m / 1000).toFixed(1).replace('.', ',')} km`
}

/** Couleur de repli quand le compte vise n'en donne aucune. */
const TEINTE_PAR_DEFAUT = 0xffb43f

// -------------------------------------------------------------------- vue

export class Boussole {
  private readonly racine: HTMLDivElement
  private readonly cadran: HTMLButtonElement
  private readonly aiguille: HTMLElement
  private readonly orbiteNord: HTMLElement
  private readonly lettreNord: HTMLElement
  private readonly orbiteCible: HTMLElement
  private readonly lecture: HTMLElement
  private readonly nom: HTMLElement
  private readonly distance: HTMLElement

  /** Ordre du tour de la derniere image: c'est lui que le clic parcourt. */
  private ids: readonly string[] = []
  /** Qui l'on vise, ou `null` pour personne. */
  private idCible: string | null = null
  /**
   * Vrai des que le joueur a touche le cadran.
   *
   * Sans ce drapeau, « je ne vise personne » et « je n'ai encore rien choisi »
   * seraient le meme etat: on remettrait donc une cible tout seul juste apres
   * que le joueur a range la marque, et le dernier cran du tour serait
   * inatteignable.
   */
  private choixFait = false

  /** Derniers etats ecrits: on n'ecrit pas soixante fois la meme chaine. */
  private angleNord = NaN
  private angleCap = NaN
  private texteNom = ''
  private texteDistance = ''
  private teinte = ''
  private capVisible = false
  private capAEcran = false
  private seul = true

  constructor(parent: HTMLElement) {
    this.racine = el('div', 'boussole')

    // Le cadran ENTIER est le bouton: le disque et la lecture forment un seul
    // instrument, et un nom a toucher agrandit la cible tactile sur telephone.
    this.cadran = el('button', 'boussole__cadran')
    this.cadran.type = 'button'
    this.cadran.setAttribute('aria-live', 'polite')

    const rose = el('span', 'boussole__rose')

    this.aiguille = el('span', 'boussole__aiguille')
    this.aiguille.append(el('i', 'boussole__pointe'), el('i', 'boussole__queue'))

    // La lettre voyage au bout de l'aiguille, mais elle se lit toujours a
    // l'endroit: l'orbite tourne, la lettre tourne du meme angle en sens
    // inverse. Un « N » couche est un « Z ».
    this.orbiteNord = el('span', 'boussole__orbite')
    this.lettreNord = el('span', 'boussole__nord', 'N')
    this.orbiteNord.append(this.lettreNord)

    // L'orbite de la cible est INTERIEURE au disque, celle du « N » exterieure:
    // les deux marques ne se marchent jamais dessus, meme au meme angle.
    this.orbiteCible = el('span', 'boussole__cible')
    this.orbiteCible.append(el('i', 'boussole__pastille'))

    rose.append(el('i', 'boussole__pivot'), this.aiguille, this.orbiteNord, this.orbiteCible)

    this.lecture = el('span', 'boussole__lecture')
    this.nom = el('span', 'boussole__nom', '')
    this.distance = el('span', 'boussole__distance', '')
    this.lecture.append(el('i', 'boussole__jeton'), this.nom, this.distance)

    this.cadran.append(rose, this.lecture)
    this.racine.append(this.cadran)
    // On demarre seul: l'etat de depart est ECRIT, il ne s'obtient pas a la
    // premiere image — sinon un jeu solo garderait un cadran qui promet un
    // geste dont il n'a rien a faire.
    this.cadran.classList.add('boussole__cadran--seul')
    this.appliquerAbsence()

    this.cadran.addEventListener('click', () => this.cycler())

    parent.append(this.racine)
    journal.info('menu', 'boussole en place')
  }

  /**
   * A appeler a chaque image.
   *
   * `autres` est la liste COMPLETE des connectes qu'on sait situer, pas
   * seulement ceux qui ont un corps a l'ecran: c'est justement celui qui est
   * trop loin pour etre dessine qu'on cherche.
   */
  maj(
    azimut: number,
    moi: { x: number; z: number },
    autres: readonly JoueurVise[],
    camera: THREE.Camera,
  ): void {
    if (Number.isFinite(azimut)) {
      const n = Math.round(angleEcran(azimut, NORD.dx, NORD.dz) * 10) / 10
      if (n !== this.angleNord) {
        this.angleNord = n
        this.aiguille.style.transform = `rotate(${n}deg)`
        this.orbiteNord.style.transform = `rotate(${n}deg)`
        this.lettreNord.style.transform = `rotate(${-n}deg)`
      }
    }

    const liste = ordonnerVises(autres)
    this.ids = liste.map((j) => j.id)
    const cible = this.resoudre(liste)
    const cap = cible ? capVers(moi, cible) : null

    if (this.seul !== (liste.length === 0)) {
      this.seul = liste.length === 0
      this.cadran.classList.toggle('boussole__cadran--seul', this.seul)
    }

    const montrer = cap !== null
    if (montrer !== this.capVisible) {
      this.capVisible = montrer
      this.appliquerAbsence()
    }
    if (!cap || !cible) {
      this.cadran.setAttribute('aria-label', 'Boussole : l’aiguille rouge montre le nord')
      return
    }

    if (cap.distance > 0.05) {
      const a = Math.round(angleEcran(azimut, cap.dx, cap.dz) * 10) / 10
      if (a !== this.angleCap) {
        this.angleCap = a
        this.orbiteCible.style.transform = `rotate(${a}deg)`
      }
    }

    const teinte = hex(cap.couleur ?? TEINTE_PAR_DEFAUT)
    if (teinte !== this.teinte) {
      this.teinte = teinte
      this.racine.style.setProperty('--cible-teinte', teinte)
    }

    const nom = cap.nom ?? 'Joueur'
    const d = formaterDistance(cap.distance)
    if (nom !== this.texteNom || d !== this.texteDistance) {
      if (nom !== this.texteNom) this.nom.textContent = nom
      if (d !== this.texteDistance) this.distance.textContent = d
      this.texteNom = nom
      this.texteDistance = d
      this.cadran.setAttribute(
        'aria-label',
        `Boussole : le nord, et ${nom} à ${d}. Touche-la pour viser un autre joueur.`,
      )
    }

    /*
     * On GARDE la marque quand le joueur vise est deja a l'ecran: c'est une
     * cible choisie a la main, l'effacer ferait croire que le clic n'a rien
     * fait. On l'attenue seulement, pour dire « il est la, leve les yeux ».
     */
    const aEcran = visibleAEcran(cible, camera)
    if (aEcran !== this.capAEcran) {
      this.capAEcran = aEcran
      this.cadran.classList.toggle('boussole__cadran--vue', aEcran)
    }
  }

  /** Cache tout (mode photo, panneau plein ecran). */
  montrer(v: boolean): void {
    this.racine.classList.toggle('boussole--cachee', !v)
  }

  detruire(): void {
    this.racine.remove()
  }

  /**
   * Qui l'on vise cette image-ci, en tenant compte des departs.
   *
   * Tant que le joueur n'a rien choisi, on lui montre le premier du tour: la
   * boussole se presente toute seule quand quelqu'un se connecte, sans quoi
   * personne ne devinerait qu'elle sait faire ca.
   */
  private resoudre(liste: readonly JoueurVise[]): JoueurVise | null {
    if (liste.length === 0) {
      // Plus personne: on oublie aussi le choix, pour que le prochain arrivant
      // soit annonce au lieu d'attendre un clic.
      this.idCible = null
      this.choixFait = false
      return null
    }
    if (this.idCible !== null && !this.ids.includes(this.idCible)) {
      const parti = this.idCible
      this.idCible = cibleDeRepli(this.ids, parti)
      journal.info('boussole', 'cible deconnectee, on passe au suivant', { parti, vers: this.idCible })
    }
    if (this.idCible === null && !this.choixFait) this.idCible = this.ids[0] ?? null
    return liste.find((j) => j.id === this.idCible) ?? null
  }

  /** Un cran de plus dans le tour des connectes. */
  private cycler(): void {
    // Le geste est journalise meme quand il ne mene nulle part — c'est ce que
    // le joueur a fait — mais il ne CLIQUE pas dans le vide: un son qui repond
    // a un cadran qui ne change pas ferait croire a une panne.
    if (this.ids.length === 0) {
      journal.input('tap', "boussole: personne d'autre en ligne")
      return
    }
    clicSonore()
    const avant = this.idCible
    this.choixFait = true
    this.idCible = cibleSuivante(this.ids, this.idCible)
    // INPUT et pas INFO: c'est un geste du joueur, il doit se relire dans le
    // fichier de session comme n'importe quel tap (voir CLAUDE.md).
    journal.input('tap', 'boussole: joueur vise', {
      de: avant ?? 'personne',
      vers: this.idCible ?? 'personne',
      connectes: this.ids.length,
    })
    this.angleCap = NaN
  }

  /** Range ou sort la lecture selon qu'on vise quelqu'un. */
  private appliquerAbsence(): void {
    this.lecture.classList.toggle('boussole__lecture--absente', !this.capVisible)
    this.orbiteCible.classList.toggle('boussole__cible--absente', !this.capVisible)
  }
}
